import { Injectable, EventEmitter, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { fromEvent, merge, Observable, of } from 'rxjs';

import { TokenProvider, ShellDomainProvider, UserInfoService } from '@ecap3/core';

import { SignalrUtility } from '../utility/signalr-utility';
import { SignalrNotificationHub } from './signalr-notification-hub';
import { ISubscriptionFilter, SignalrNotificationConstant } from '../constant/signalr-notification.constant';
import { map } from 'rxjs/operators';

export enum HubConnectionState {
    Connecting = 1,
    Connected = 2,
    Reconnecting = 3,
    Disconnected = 4
}
export let signalrUtility = <any>new SignalrUtility();

@Injectable()
export class SignalrNotificationService {
    hub: any;
    isHubConnected: boolean = false;
    public static storage = signalrUtility.storage;
    public static topicGenerator = signalrUtility.topicGenerator;
    public static weight = signalrUtility.weight;
    public static reconnectCounter = 0;
    public requestPipeline: any[] = [];
    private signalRConnectionEstablished = new EventEmitter<boolean>();
    hubConnectionEstablished = this.signalRConnectionEstablished.asObservable();
    online$: Observable<boolean>;
    wasOffline = false;

    constructor(private tokenProvider: TokenProvider,
        private userInfoService: UserInfoService,
        @Inject(PLATFORM_ID) private platformId: Object
    ) {
        if (isPlatformBrowser(this.platformId)) {
            this.isHubConnected = false;
            (<any>this).userInfoService.currentUserData.subscribe({
                next: (response: any) => {
                    if (response == 'LOG_IN') {
                        this.hubInitialization();
                    }
                    else if (response == 'LOG_OUT') {
                        this.disconnect();
                    }
                    else {
                        this.hubInitialization();
                    }
                }
            });

            this.online$ = merge(
                of(navigator.onLine),
                fromEvent(globalThis, "online").pipe(map(() => true)),
                fromEvent(globalThis, "offline").pipe(map(() => false))
            );
            this.online$
                .subscribe({
                    next: (response) => {
                        if (response) {
                            this.reconnectNotification();
                        } else {
                            this.wasOffline = true;
                        }
                    }
                });

        }
    }

    ngOnInit() {


    }

    registerBroadcast(key, callback) {
        signalrUtility.registerBroadcast(key, callback);
        let subscriptionFilters = { "SubscriptionFilters": [{ Context: key, ActionName: "", Value: "" }] };
        if (this.hub && this.hub.connection && this.hub.connection.state === "Connected") {
            if (this.hub.connection.invoke) this.hub.connection.invoke('Subscribe', JSON.stringify(subscriptionFilters));
        } else {
            this.storeRequestContextInQueue(callback, subscriptionFilters);
        }
    }

    registerBroadcastWithFilters(filter: ISubscriptionFilter, callback) {
        signalrUtility.registerFilterBroadcast(filter, callback);
        let subscriptionFilters = { "SubscriptionFilters": [filter] };
        if (this.hub && this.hub.connection && this.hub.connection.state === "Connected") {
            if (this.hub.connection.invoke) this.hub.connection.invoke('Subscribe', JSON.stringify(subscriptionFilters));
        } else {
            this.storeRequestContextInQueue(callback, subscriptionFilters);
        }
    }

    subscribeRequestContextFromQueue() {
        this.requestPipeline.forEach((request) => {
            if (this.hub && this.hub.connection && this.hub.connection.invoke) this.hub.connection.invoke('Subscribe', JSON.stringify(request.context));
        });
        this.requestPipeline = [];
    }

    storeRequestContextInQueue(callback, requestContext) {
        const duplicateContext = this.requestPipeline.find(x => x.context == requestContext);
        if (!duplicateContext) {
            this.requestPipeline.push({
                callback: callback,
                context: requestContext
            });
        }
    }

    subscribeAll(context, action, callback) {
        let model = {
            context: context,
            action: signalrUtility.populateActions(action)
        };

        this.subscribe(model, callback);
    }

    subscribeSingle(context, id, action, callback) {
        let model = {
            context: context,
            id: id,
            action: signalrUtility.populateActions(action)
        };

        this.subscribe(model, callback);
    }

    subscribeGroup(context, id, action, callback) {
        let model = {
            context: context,
            id: id,
            action: signalrUtility.populateActions(action)
        };

        this.subscribe(model, callback);
    }

    subscribe(model, callback) {
        if (!model)
            throw '{NotificationManager.subscribe} undefined model';
        if (!callback || "function" !== typeof callback)
            throw '{NotificationManager.subscribe} illegal callback';
        let ntopic = SignalrNotificationService.topicGenerator.normalize(model, SignalrNotificationService.weight);
        let topic = SignalrNotificationService.topicGenerator.generate(ntopic);
        let timestamp = Date.now();
        let subscriptionToken = [topic, timestamp].join(SignalrNotificationConstant.SEPERATOR[0]);

        SignalrNotificationService.storage.write(subscriptionToken, callback);

        let serverModel = signalrUtility.registerSubscription(model, callback);
        if (this.hub && this.hub.connection && this.hub.connection.state === "Connected") {
            if (this.hub.connection.invoke) this.hub.connection.invoke('Subscribe', serverModel);
        }
        else {
            this.storeRequestContextInQueue(callback, JSON.parse(serverModel));
        }

        // NOTE this line of code below has no obvious reason to live. Perhaps it was writter to solve the hirarchical travarsal problem. If no obvious errors are found remove this.
        SignalrNotificationService.storage.write(timestamp, signalrUtility.outgoingFormatProvider(model));

        return (function () {
            this.cancelSubscribtion(subscriptionToken);
        }).bind(this);
    }

    cancelSubscribtion(subscriptionToken) {
        if (!subscriptionToken)
            throw "{SignalRNotificationManager.subscribe} undefined subscriptionToken";

        SignalrNotificationService.storage.prune(subscriptionToken);
        let timestamp = subscriptionToken.slice(subscriptionToken.lastIndexOf(SignalrNotificationConstant.SEPERATOR[0]) + 1);
        this.hub.connection.invoke('Unsubscribe', SignalrNotificationService.storage.read(timestamp));
    }

    unSubscribe(context, id, action, callback) {
        let model = {
            context: context,
            id: id,
            action: signalrUtility.populateActions(action)
        };

        if (!model)
            throw '{SignalRNotificationManager.unSbscribe} undefined model';

        let serverModel = JSON.stringify(signalrUtility.outgoingFormatProvider({ ...model }));

        let topic = SignalrNotificationService.topicGenerator.generate(SignalrNotificationService.topicGenerator.normalize(model, SignalrNotificationService.weight));
        SignalrNotificationService.storage.prune(topic);

        this.hub.connection.invoke('Unsubscribe', serverModel); //Calling a server method
        callback('Success');
    }

    disconnect() {
        //	this.hub['disconnect']();
        if (this.hub && this.hub.connection) {
            this.hub.connection.stop();
        }
    }

    reconnect() {
        if (this.hub && this.hub['reconnect']) {
            this.hub['reconnect'](function (response) {
                SignalrNotificationService.reconnectCounter++;
            });
        }
        else
            this.hubInitialization();
    }

    hubInitialization() {
        let that = this;
        that.hub = new SignalrNotificationHub(ShellDomainProvider['api'].NotificationHubName, {
            listeners: {
                'Notify': function (message) {
                    let model = signalrUtility.incomingFormatProvider(message);
                    let topic = SignalrNotificationConstant.NOTIFICATIONSCOPE.FILTER === message.NotificationType
                        ? SignalrNotificationService.topicGenerator.generate(SignalrNotificationService.topicGenerator.normalize(model, SignalrNotificationService.weight))
                        : message.ResponseKey;
                    let callbacks = SignalrNotificationService.storage.read(topic) || Object.create(null);

                    let invokeValue = {
                        key: message.ResponseKey,
                        value: message.ResponseValue
                    };
                    let keys = Object.keys(callbacks);
                    for (let i = 0; i < keys.length; i++)
                        callbacks[keys[i]](invokeValue);
                }
            },
            methods: ['Subscribe', 'Unsubscribe'],
            //queryParams: 'Authorization=' + 'bearer ' + response.access_token,
            rootPath: ShellDomainProvider['api'].NotificationService,
            useSharedConnection: false,
            stateChanged: function (state) {
                switch (state.newState) {
                    case HubConnectionState.Connecting:
                        that.signalRConnectionEstablished.emit(true);
                        that.isHubConnected = true;
                        // console.log('connecting');
                        break;
                    case HubConnectionState.Connected:
                        // console.log('connected');
                        break;
                    case HubConnectionState.Reconnecting:
                        // console.log('reconnecting');
                        break;
                    case HubConnectionState.Disconnected:
                        that.isHubConnected = false;
                        // console.log('disconnected');
                        break;
                }
            },
            transport: ShellDomainProvider['api'].TransportMedium || ['webSockets', 'foreverFrame', 'serverSentEvents', 'longPolling'],
            autoConnect: true,
            logging: true,
            withCredentials: true,
            jsonp: false,
            errorHandler: (error) => {
                //this.getToken();
                if (SignalrNotificationService.reconnectCounter == 0)
                    that.reconnect();
                else
                    SignalrNotificationService.reconnectCounter--;
            }
        });

        this.hub.connection.start().then(() => {
            if (this.hub && this.hub.connection && this.hub.connection.state === "Connected") {
                this.signalRConnectionEstablished.emit(true);
                this.isHubConnected = true;
                this.subscribeRequestContextFromQueue();
            }
        });

        this.hub.connection.on('Notify', function (message) {
            let keyLowerCase = signalrUtility.toLowerCaseNotificationReturnObject(message);
            let model = signalrUtility.incomingNotificationFormatProvider(keyLowerCase);
            let callbacks = {};

            if (SignalrNotificationConstant.NOTIFICATIONSCOPE.FILTER == keyLowerCase.notificationtype) {
                let ntopic = SignalrNotificationService.topicGenerator.normalize(model, SignalrNotificationService.weight);
                let topic = SignalrNotificationService.topicGenerator.generate(ntopic);
                callbacks = SignalrNotificationService.storage.read(topic) || Object.create(null);
            }
            else {
                let topic = model.responsekey;
                callbacks = SignalrNotificationService.storage.read(topic);
                if (!callbacks) {
                    callbacks = SignalrNotificationService.storage.read(keyLowerCase.responsekey) || Object.create(null);
                    model.id = undefined;
                }
            }

            let invokeValue = {
                key: message.responseKey,
                value: message.responseValue,
                denormalizedMessage: message.denormalizedPayload
            };

            let keys = Object.keys(callbacks);
            for (let i = 0; i < keys.length; i++)
                callbacks[keys[i]](invokeValue);
        });
    }

    reconnectNotification() {
        if(!this.wasOffline) return;

        console.log("Reconnecting....");
        this.tokenProvider.refreshTokenObservable().subscribe({
            next: (newToken: any) => {
                this.disconnect();
                this.hubInitialization();
            }
        });
        this.wasOffline = false;
    }
}